import { bootstrapApplication } from '@angular/platform-browser';
import { RouteReuseStrategy, provideRouter, withPreloading, PreloadAllModules, withRouterConfig } from '@angular/router';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { IonicStorageModule } from '@ionic/storage-angular';
import { importProvidersFrom, ErrorHandler, Injectable, inject } from '@angular/core';
import { provideHttpClient } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { addIcons } from 'ionicons';
import * as allIcons from 'ionicons/icons';

import { routes } from './app/app.routes';
import { AppComponent } from './app/app.component';
import { isDevMode } from '@angular/core';
import { provideServiceWorker } from '@angular/service-worker';
import { StorageService } from './services/storage.service';
import { UtilsService } from './services/utils.service';

// Global error handler that sends uncaught crashes to Loggly via UtilsService
@Injectable()
class GlobalErrorHandler implements ErrorHandler {
  private utils = inject(UtilsService);

  handleError(error: any): void {
    // extract useful info from the error
    const message = error?.message || error?.toString() || 'Unknown uncaught error';
    const stack = error?.stack || '';
    const route = window?.location?.pathname || '';

    this.utils.logError('UNCAUGHT: ' + message, {
      stack: stack,
      route: route,
      type: error?.name || 'Error',
    });

    // still log to console for local debugging
    console.error('Uncaught error:', error);
  }
}

// Register all Ionicons (16kb total - good for development and small overhead)
addIcons(allIcons);

bootstrapApplication(AppComponent, {
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideIonicAngular({
      mode: 'md', // This forces Material Design styling across all platforms
      platform: {
        /** Override mobile detection to allow URL parameter simulation */
        'mobile': (win: Window) => {
          // Check for URL parameter override first (for testing)
          const urlParams = new URLSearchParams(win.location.search);
          const platform = urlParams.get('ionicplatform');
          if (platform === 'ios' || platform === 'android') {
            return true;
          }
          // Fall back to default mobile detection
          const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(win.navigator.userAgent);
          return isMobile;
        },
        /** Override Android detection for URL parameter simulation */
        'android': (win: Window) => {
          const urlParams = new URLSearchParams(win.location.search);
          const platform = urlParams.get('ionicplatform');
          if (platform === 'android') {
            return true;
          }
          // Fall back to default Android detection
          return /Android/i.test(win.navigator.userAgent);
        },
        /** Override iOS detection for URL parameter simulation */
        'ios': (win: Window) => {
          const urlParams = new URLSearchParams(win.location.search);
          const platform = urlParams.get('ionicplatform');
          if (platform === 'ios') {
            return true;
          }
          // Fall back to default iOS detection
          return /iPhone|iPad|iPod/i.test(win.navigator.userAgent);
        }
      }
    }),
    provideRouter(routes,
      withPreloading(PreloadAllModules),
      withRouterConfig({
        defaultQueryParamsHandling: 'preserve'
      })
    ), 
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerImmediately'
    }),
    provideHttpClient(),
    importProvidersFrom(
      IonicStorageModule.forRoot(),
      TranslateModule.forRoot()
    ),
    provideTranslateHttpLoader({
      prefix: './assets/i18n/',
      suffix: '.json'
    }),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: 'STORAGE_SERVICE', useClass: StorageService },
  ],
});
